/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable react/no-unknown-property */
import { IComponent, IComponentProps } from '@msdyn365-commerce/core';
import { getByIdsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import React, { useRef, useState } from 'react';
import { FaPen, FaCheck, FaTrashAlt } from 'react-icons/fa';
import { MAORI_LANG } from '../utilities/constants';

export interface ICollectionItemComponentProps extends IComponentProps<{}> {
    className?: string;
    index: number;
    isCheckboxEnabled: boolean;
    onCheckboxSelect: (event: React.ChangeEvent<HTMLInputElement>, collectionId: string, isChecked: boolean) => void;
    collection: any;
    isClickCollectionEnabled: boolean;
    onClickCollection: (index: number, collectionId: number, collectionProducts: any[]) => void;
    isEditEnabled: boolean;
    isDeleteEnabled: boolean;
    isTickMarkVisible: boolean;
    toggleEditableCollection: (event: any, index: any) => void;
    deleteCollection: (event: any, collectionId: any) => void;
    collectionNameRef?: any;
    currentIndex?: any;
    selectCollection: (index: number, collectionId: number) => void;
    updateCollectionName: (collectionId: number, changedCollectionName: string, setCollectionTitle: any) => void;
    selectedCollection?: any;
    redirectionLink?: any;
    setErrorMessage?: any;
    MoeErrorMessages?: any;
    preferredLanguageForError?: string;
    siteLang?: string;
    otherSiteCollectionCount?: any;
}

export interface ICollectionItemComponent extends IComponent<ICollectionItemComponentProps> {}

export const CollectionItem: React.FC<ICollectionItemComponentProps> = ({
    context,
    typeName,
    id,
    className,
    index,
    isCheckboxEnabled,
    onCheckboxSelect,
    collection,
    isClickCollectionEnabled,
    onClickCollection,
    isEditEnabled,
    isDeleteEnabled,
    isTickMarkVisible,
    toggleEditableCollection,
    deleteCollection,
    currentIndex,
    selectCollection,
    selectedCollection,
    updateCollectionName,
    setErrorMessage,
    MoeErrorMessages,
    preferredLanguageForError,
    siteLang,
    otherSiteCollectionCount
}) => {
    // variables declarations
    const titleLength = 25;
    const apiSettings = context.request.apiSettings;

    // State definitions
    const [isDisabled, setIsDisabled] = useState(true);
    const [collectionTitle, setCollectionTitle] = useState(collection.Name || '');
    const [backgroundImage, setBackgroundImage] = useState<any>('');
    const [isChecked, setIsChecked] = useState(false);

    // Ref definitions
    const collectionNameRef: any = useRef();

    // Language change variables
    const languageAttributes = {
        noOfResourcesText: 'resources',
        noOfResourcesTextInMaori: 'Ngā rauemi',
        editText: 'Edit collection',
        editTextInMaori: 'Ētitahia te kohinga',
        deleteText: 'Delete collection',
        deleteTextInMaori: 'Mukua te kohinga'
    };

    /* @note:
        methods for add to collection functionality
    */
    const toggleEdit = (event: any) => {
        event?.stopPropagation;
        event?.preventDefault;
        setIsDisabled(!isDisabled);
        collectionNameRef?.current?.focus();
    };

    const onUpdateCollectionName = () => {
        event?.stopPropagation;
        event?.preventDefault;
        setIsDisabled(!isDisabled);
        console.log('updateCollectionName');
        if (collectionNameRef?.current?.value === '') {
            setCollectionTitle(makeid(5));
        }

        if (collectionNameRef?.current?.value === collection.Name) {
            setErrorMessage(
                preferredLanguageForError === 'english' ? MoeErrorMessages.duplicateName : MoeErrorMessages.duplicateNameInMaori
            );
            return;
        }

        if (collectionNameRef?.current?.value?.length > 25) {
            setErrorMessage(
                preferredLanguageForError === 'english'
                    ? MoeErrorMessages.exceededCollectionNameLimit
                    : MoeErrorMessages.exceededCollectionNameLimitInMaori
            );
            setCollectionTitle(collection.Name);
            return;
        }
        updateCollectionName(collection.Id, collectionNameRef?.current?.value, setCollectionTitle);
    };

    const onCollectionTitleInputClick = (event: any) => {
        event?.stopPropogation;
        event?.preventDefault;
    };

    const changeCollectionName = (event: any) => {
        setCollectionTitle(collectionNameRef.current.value);
    };

    const makeid = (length: number) => {
        var result = '';
        var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        var charactersLength = characters.length;
        for (var i = 0; i < length; i++) {
            result += characters.charAt(Math.floor(Math.random() * charactersLength));
        }
        return result;
    };

    const generateImageUrl = (imageUrl: any, apiSettings: any) => {
        if (imageUrl) {
            // Images hosted in CMS include schema
            if (typeName === 'add') {
                if (imageUrl.startsWith('http')) {
                    return `${imageUrl}&w=225&h=225&q=80&m=6&f=jpg&cropfocalregion=true`;
                }
            } else {
                if (imageUrl.startsWith('http')) {
                    return `${imageUrl}&w=250&h=250&q=80&m=6&f=jpg&cropfocalregion=true`;
                }
            }
            // Images hosted in Retail Server must be encoded and joined with the base image url
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            const preImageUrl = apiSettings.baseImageUrl + encodeURIComponent(imageUrl);
            if (typeName === 'add') {
                return `${preImageUrl}&w=225&h=225&q=80&m=6&f=jpg&cropfocalregion=true`;
            } else {
                return `${preImageUrl}&w=250&h=250&q=80&m=6&f=jpg&cropfocalregion=true`;
            }
        } else {
            // Unable to generate a proper Image URL for Product
            return undefined;
        }
    };

    const getBackgroundImage = async () => {
        const commerceList = collection.CommerceListLines;

        if (commerceList && backgroundImage !== false) {
            if (commerceList?.length > 0) {
                const productId = commerceList[0]?.ProductId;
                const channelId = context?.request?.apiSettings?.channelId;
                const actionContext: any = context?.actionContext;
                const productDetails = await getByIdsAsync(actionContext, channelId, [productId]);
                const productImageUrl = generateImageUrl(productDetails[0]?.PrimaryImageUrl, apiSettings);
                setBackgroundImage(productImageUrl);
            }
        }
    };

    void getBackgroundImage();

    const toggleCheckbox = (event: any) => {
        setErrorMessage('');
        event.stopPropagation;
        if (collection?.CommerceListLines?.length === 0) {
            setErrorMessage(
                preferredLanguageForError === 'english'
                    ? MoeErrorMessages.noEmptyCollectionSharing
                    : MoeErrorMessages.noEmptyCollectionSharingInMaori
            );
            return;
        }
        onCheckboxSelect(event, collection.Id, !isChecked);
        setIsChecked(!isChecked);
        console.log(isChecked);
    };

    const onCollectionClick = () => {
        if (typeName === 'add') {
            console.log('ADD Collection');
            selectCollection(index, collection?.Id);
        }
        if (typeName === 'view') {
            console.log('View COllection');
            isClickCollectionEnabled && onClickCollection(index, collection.Id, collection.CommerceListLines);
        }
        if (typeName === 'pending') {
            onClickCollection(index, collection.Id, collection.CommerceListLines);
        }
        setTimeout(() => {
            (document?.querySelector('.moe-btn--add-to-collection') as HTMLButtonElement)?.focus();
        }, 0);
    };

    const getCollectionCount = () => {
        let count: number = 0;
        if (collection?.CommerceListLines?.length) {
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            count = count + collection.CommerceListLines.length;
        }
        if (otherSiteCollectionCount && otherSiteCollectionCount[collection.Id]) {
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            count = count + otherSiteCollectionCount[collection.Id];
        }

        return count;
    };

    // End variables
    // const bgProductImage = {
    //     backgroundImage: `url(${backgroundImage})`
    // };

    return (
        <>
            <div className='moe-collection-tile' tabIndex={0}>
                <div className='moe-collection-tile-item--container'>
                    {isCheckboxEnabled && (
                        <label className='checkbox-container'>
                            <input
                                checked={isChecked}
                                type='checkbox'
                                className='view-collecton-check-box'
                                id={`cb${index}`}
                                onChange={toggleCheckbox}
                                aria-label={`Select ${collectionTitle}`}
                                tabIndex={0}
                            />
                            <span className='checkmark'></span>
                        </label>
                    )}
                    <div
                        // style={bgProductImage}
                        className={`moe-collection-tile-item ${className ? className : ' msc-collection-tile-item'} ${
                            selectedCollection?.collectionId === collection?.Id
                                ? 'msc-collection-border__blue'
                                : 'msc-collection-border__white'
                        }`}
                        tabIndex={0}
                        onClick={onCollectionClick}
                        onKeyPress={e => (e.charCode === 13 ? onCollectionClick() : null)}
                    >
                        {backgroundImage && (
                            <img
                                className='moe-collection-tile-item__image'
                                onError={(event: any) => setBackgroundImage(false)}
                                src={backgroundImage}
                                alt={collectionTitle}
                            />
                        )}
                    </div>
                </div>

                <div className='msc-collection__collection'>
                    <div>
                        <div className='msc-collection__details'>
                            {!isDisabled ? (
                                <input
                                    ref={collectionNameRef}
                                    onChange={(event: any) => changeCollectionName(event)}
                                    onClick={onCollectionTitleInputClick}
                                    disabled={isDisabled}
                                    className='msc-collection-modal__title tooltip-test'
                                    title={collectionTitle}
                                    tabIndex={0}
                                    value={collectionTitle}
                                />
                            ) : (
                                <p className='msc-collection-modal__title tooltip-test' title={collectionTitle} tabIndex={0}>
                                    {`${collectionTitle.slice(0, titleLength)}${collectionTitle.length > titleLength ? '...' : ''}`}
                                </p>
                            )}
                        </div>
                        <div className='msc-collection__resources' tabIndex={0}>
                            {getCollectionCount() || 0}{' '}
                            {siteLang === MAORI_LANG ? languageAttributes?.noOfResourcesTextInMaori : languageAttributes?.noOfResourcesText}
                        </div>
                    </div>

                    <div className='ms-addcollection__crud '>
                        {isEditEnabled && (
                            <div className='msc-edit__collection' onClick={(event: any) => toggleEditableCollection(event, index)}>
                                {isDisabled ? (
                                    <FaPen
                                        className='tooltip-test'
                                        title={siteLang === MAORI_LANG ? languageAttributes.editTextInMaori : languageAttributes.editText}
                                        onClick={toggleEdit}
                                        onKeyPress={(event: any) => (event.charCode === 13 ? toggleEdit(event) : null)}
                                        tabIndex={0}
                                        role='button'
                                    />
                                ) : (
                                    <FaCheck
                                        role='button'
                                        onClick={onUpdateCollectionName}
                                        onKeyPress={e => {
                                            e.charCode ? onUpdateCollectionName() : null;
                                        }}
                                        tabIndex={0}
                                    />
                                )}
                            </div>
                        )}
                        {isDeleteEnabled && (
                            <div
                                onClick={(event: any) => deleteCollection(event, collection?.Id)}
                                onKeyPress={(event: any) => {
                                    console.log('test99615 - calling deleteCollection from onKeyPress', event, event.charCode);
                                    event.charCode === 13 ? deleteCollection(event, collection?.Id) : null;
                                }}
                                className='msc-delete__collection'
                                tabIndex={0}
                            >
                                <FaTrashAlt
                                    className='tooltip-test'
                                    title={siteLang === MAORI_LANG ? languageAttributes.deleteTextInMaori : languageAttributes.deleteText}
                                    role='button'
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
